import React, { useCallback } from 'react';
import { TokenStatus } from '../stores/data/types';
import { TOKEN_STATUS_LABELS } from '../stores/data/types/feTokenState';
import { feManagementSystemApi, retrospectiveApi } from '../api/fe-management-system-api';
import { useToasterStore } from '../stores/ui/toasterStore';
import getApiErrorMessage from '../utils/getApiErrorMessage';

export const statusOptions: TokenStatus[] = [
  TokenStatus.NONE,
  TokenStatus.FETCHING_DATA,
  TokenStatus.REVIEWING,
  TokenStatus.READY_FOR_IC,
  TokenStatus.BUY,
  TokenStatus.NOT_BUY,
  TokenStatus.HOLD,
  TokenStatus.LATER,
  TokenStatus.SOLD,
];

export const getStatusChipStyle = (status: TokenStatus | undefined): string => {
  if (!status || status === TokenStatus.NONE) {
    return 'bg-gray-800 border-gray-600 text-gray-400';
  }
  switch (status) {
    case TokenStatus.FETCHING_DATA:
      return 'bg-blue-600 border-blue-400 text-white';
    case TokenStatus.FETCHING_FAILED:
      return 'bg-red-600 border-red-400 text-white';
    case TokenStatus.REVIEWING:
      return 'bg-yellow-700 border-yellow-500 text-white';
    case TokenStatus.READY_FOR_IC:
      return 'bg-cyan-400 border-cyan-300 text-white';
    case TokenStatus.BUY:
      return 'bg-green-700 border-green-500 text-white';
    case TokenStatus.NOT_BUY:
      return 'bg-red-700 border-red-500 text-white';
    case TokenStatus.HOLD:
    case TokenStatus.LATER:
      return 'bg-gray-700 border-gray-500 text-white';
    case TokenStatus.SOLD:
      return 'bg-red-800 border-red-600 text-white';
    default:
      return 'bg-gray-800 border-gray-600 text-gray-400';
  }
};

interface StatusChipProps {
  tokenAddress?: string;
  status: TokenStatus | undefined;
  className?: string;
  onStatusChange?: (status: TokenStatus) => void;
  onSuccess?: () => void;
}

const StatusChip: React.FC<StatusChipProps> = ({ 
  tokenAddress, 
  status, 
  className = '', 
  onStatusChange,
  onSuccess 
}) => {
  const { showToaster } = useToasterStore();
  
  const handleInternalStatusChange = useCallback(
    async (nextStatus: TokenStatus) => {
      // If tokenAddress is provided, we handle the update internally.
      // If only onStatusChange is provided, we delegate to the parent.
      if (!tokenAddress) {
        if (onStatusChange) onStatusChange(nextStatus);
        return;
      }

      const previousStatus = status;
      try {
        await feManagementSystemApi.updateDecision(tokenAddress, nextStatus);
        showToaster(`Status updated to ${nextStatus}!`, 'success');
        
        if (onSuccess) {
          onSuccess();
        }
        
        if (onStatusChange) {
          onStatusChange(nextStatus);
        }

        if (previousStatus === TokenStatus.BUY && nextStatus === TokenStatus.SOLD) {
          const confirmed = window.confirm(
            'Did you experience a loss with this decision? Would you like to record a loss retrospective?',
          );
          if (confirmed) {
            try {
              await retrospectiveApi.reportLoss(tokenAddress);
              showToaster('Loss retrospective saved successfully.', 'success');
            } catch (error: any) {
              showToaster(
                getApiErrorMessage(error, 'Failed to save loss retrospective.'),
                'error',
              );
            }
          }
        }
      } catch (error: any) {
        console.error('상태 업데이트 실패:', error);
        showToaster(
          getApiErrorMessage(error, 'Failed to update status.'),
          'error'
        );
      }
    },
    [tokenAddress, status, onStatusChange, onSuccess, showToaster]
  );

  const allowEdit = !!tokenAddress || typeof onStatusChange === 'function';
  const transitionMap: Record<TokenStatus, TokenStatus[]> = {
    [TokenStatus.NONE]: [TokenStatus.NONE, TokenStatus.BUY, TokenStatus.NOT_BUY, TokenStatus.HOLD, TokenStatus.LATER],
    [TokenStatus.FETCHING_DATA]: [TokenStatus.FETCHING_DATA, TokenStatus.LATER],
    [TokenStatus.FETCHING_FAILED]: [TokenStatus.FETCHING_FAILED, TokenStatus.LATER],
    [TokenStatus.REVIEWING]: [TokenStatus.REVIEWING, TokenStatus.NOT_BUY, TokenStatus.LATER, TokenStatus.BUY, TokenStatus.HOLD],
    [TokenStatus.READY_FOR_IC]: [TokenStatus.READY_FOR_IC, TokenStatus.BUY, TokenStatus.NOT_BUY, TokenStatus.HOLD, TokenStatus.LATER],
    [TokenStatus.BUY]: [TokenStatus.BUY, TokenStatus.SOLD, TokenStatus.LATER, TokenStatus.HOLD, TokenStatus.NOT_BUY],
    [TokenStatus.NOT_BUY]: [TokenStatus.NOT_BUY, TokenStatus.BUY, TokenStatus.HOLD, TokenStatus.LATER],
    [TokenStatus.HOLD]: [TokenStatus.HOLD, TokenStatus.BUY, TokenStatus.NOT_BUY, TokenStatus.LATER],
    [TokenStatus.LATER]: [TokenStatus.LATER, TokenStatus.BUY, TokenStatus.HOLD, TokenStatus.NOT_BUY],
    [TokenStatus.SOLD]: [TokenStatus.SOLD],
  };
  const options = transitionMap[status ?? TokenStatus.NONE] || [TokenStatus.NONE, TokenStatus.LATER];

  if (!allowEdit) {
    return (
      <span className={`inline-flex items-center justify-center min-w-[60px] h-6 px-3 rounded-full text-xs border ${getStatusChipStyle(status)} ${className}`}>
        {TOKEN_STATUS_LABELS[status ?? TokenStatus.NONE] || ''}
      </span>
    );
  }

  return (
    <select
      value={status ?? TokenStatus.NONE}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onChange={(event) => {
        event.stopPropagation();
        const nextValue = event.target.value as TokenStatus;
        handleInternalStatusChange(nextValue);
      }}
      className={`text-xs text-white border rounded-full focus:outline-none focus:ring-1 focus:ring-blue-500 ${
        status === TokenStatus.SOLD 
          ? `cursor-not-allowed ${getStatusChipStyle(status)}`
          : `cursor-pointer ${getStatusChipStyle(status)}`
      } ${className}`}
      style={{
        minWidth: 60,
        minHeight: 22,
        padding: '0 10px',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        fontSize: 9,
        lineHeight: '22px',
        textAlign: 'center',
      }}
    >
      {options.map((option) => (
        <option
          key={option}
          value={option}
          disabled={status === TokenStatus.SOLD}
        >
          {TOKEN_STATUS_LABELS[option]}
        </option>
      ))}
    </select>
  );
};

export default StatusChip;
